import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from 'oidc-react';

import Style from './headerStyle.module.scss';
import profile from '../../images/profile.png';
import { PlusCircle, Search, Bell } from 'lucide-react';
import PlanActions from '../../modules/patchcenter/PlanActions';
import { ArrowRight, Settings } from 'lucide-react';
import { LogOut } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';

import { useLocation, useNavigate } from 'react-router-dom';
import { useAllGroups, useTenant } from '../services/dashboardService';
import { useNotifications } from '../../providers/NotificationProvider/NotificationContext';
import { NotificationPanel } from '../NotificationPanel/NotificationPanel';

import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { usePermissions } from '../../hooks/usePermissions';
import { logoutUser } from '../../common/Utils/session';

interface GroupedOptions {
  label: string;
  options: string[];
}

interface DropdownProps {
  label: string;
  icon?: React.ReactNode;
  options?: (string | { label: string; options: string[] })[];
  onSelect?: (value: string) => void;
  value?: string;
  onOpen?: () => void;
}

interface HeaderProps {
  title?: string;
  dropdown?: number;
  setGroupID?: (groupID: string | null) => void;
  getGroupName?: (groupID: string | null) => string;
  showBackButton?: boolean;
  backLink?: string | null;
  sourceOptions?: string[];
  selectedSource?: string;
  onSourceChange?: (value: string) => void;
}

const dropdownStyles = {
  container: {
    backgroundColor: '#1f1f1f',
    border: '1px solid #333',
    color: '#ffffff',
    borderRadius: '0.375rem', // rounded-md
  },
  option: {
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    color: '#ffffff',
  },
  optionHover: {
    backgroundColor: '#b91c1c',
    color: '#ffffff',
  },
  placeholder: {
    color: '#a1a1a1',
  },
};

const Dropdown = ({ label, icon, options = [], onSelect, value, onOpen }: DropdownProps) => {
  // Set the default selected as the first tenant option if it exists
  const defaultSelected =
    options.length > 0 && typeof options[0] !== 'string' && options[0].options.length > 0 ? options[0].options[0] : label;

  const [selected, setSelected] = useState<string>(value ?? defaultSelected);

  useEffect(() => {
    if (value) {
      setSelected(value);
    }
  }, [value]);

  const [open, setOpen] = useState(false);

  // Update selected if options change (for async tenant fetch)
  useEffect(() => {
    if (!value && options.length > 0 && typeof options[0] !== 'string' && options[0].options.length > 0) {
      setSelected(options[0].options[0]);
    }
  }, [options, value]);

  const handleSelect = (val: string) => {
    setSelected(val);
    setOpen(false);
    onSelect?.(val);
  };

  return (
    <div className="relative">
      {/* Trigger */}
      <div
        className={`${Style.dropdown} flex items-center cursor-pointer`}
        onClick={() =>
          setOpen((prev) => {
            const next = !prev;
            if (next) onOpen?.(); // only fire when actually opening, not on close
            return next;
          })
        }
      >
        {icon && <span className="mr-2">{icon}</span>}
        <span className="ml-1 mr-1">{selected}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="10" viewBox="0 0 19 10" fill="none" className="ml-auto">
          <path
            d="M18.3316 0.272821C18.0122 -0.0466514 17.4941 -0.046706 17.1746 0.272876L9.57148 7.87613L1.96801 0.272821C1.64853 -0.0466514 1.13046 -0.046706 0.810935 0.272876C0.491407 0.592403 0.491407 1.11042 0.810935 1.42995L8.99297 9.61177C9.14641 9.76521 9.3545 9.85139 9.57148 9.85139C9.78846 9.85139 9.99661 9.76515 10.15 9.61172L18.3316 1.42989C18.6512 1.11042 18.6512 0.592349 18.3316 0.272821Z"
            fill="#828282"
          />
        </svg>
      </div>

      {/* Menu */}
      {open && (
        <div
          style={{
            ...dropdownStyles.container,
            position: 'absolute',
            marginTop: '0.25rem',
            width: '100%',
            maxHeight: '16rem',
            overflowY: 'auto',
            zIndex: 50,
          }}
        >
          {options.map((opt, i) =>
            typeof opt === 'string' ? (
              <div
                key={i}
                style={dropdownStyles.option}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownStyles.optionHover.backgroundColor)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = dropdownStyles.container.backgroundColor)}
                onClick={() => handleSelect(opt)}
              >
                {opt}
              </div>
            ) : (
              <div key={i} className="border-t first:border-t-0">
                <div style={{ ...dropdownStyles.option, fontWeight: 'bold', color: '#828282' }}>{opt.label}</div>
                {opt.options.map((o, idx) => (
                  <div
                    key={idx}
                    style={dropdownStyles.option}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownStyles.optionHover.backgroundColor)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = dropdownStyles.container.backgroundColor)}
                    onClick={() => handleSelect(o)}
                  >
                    {o}
                  </div>
                ))}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
};

const calenderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="19" height="20" viewBox="0 0 19 20" fill="none">
    <path
      d="M18.5209 5.29816C18.4071 3.40622 16.8273 1.92399 14.9246 1.92399H13.9982V1.20394C13.9982 0.80647 13.6756 0.483887 13.2782 0.483887C12.8807 0.483887 12.5581 0.80647 12.5581 1.20394V1.92399H6.7977V1.20394C6.7977 0.80647 6.47512 0.483887 6.07765 0.483887C5.68018 0.483887 5.35759 0.80647 5.35759 1.20394V1.92399H4.43124C2.52814 1.92399 0.948348 3.40622 0.83494 5.29816C0.621444 8.85666 0.625405 12.4663 0.846821 16.0266C0.959149 17.8346 2.40754 19.283 4.21559 19.3954C6.0276 19.508 7.85258 19.5642 9.67755 19.5642C11.5022 19.5642 13.3275 19.508 15.1395 19.3954C16.9476 19.283 18.396 17.8346 18.5083 16.0266C18.7301 12.4681 18.734 8.85882 18.5209 5.29816ZM17.0714 15.9373C17.0037 17.0221 16.135 17.8908 15.0506 17.9581C11.4856 18.1796 7.87022 18.1796 4.30524 17.9581C3.22048 17.8904 2.35173 17.0217 2.28441 15.9373C2.11411 13.2 2.07919 10.4339 2.17028 7.68442H17.1859C17.2766 10.435 17.2417 13.2011 17.0714 15.9373ZM6.07765 4.8042C6.47512 4.8042 6.7977 4.48162 6.7977 4.08415V3.3641H12.5581V4.08415C12.5581 4.48162 12.8807 4.8042 13.2782 4.8042C13.6756 4.8042 13.9982 4.48162 13.9982 4.08415V3.3641H14.9246C16.0669 3.3641 17.0153 4.25156 17.0833 5.38421C17.1002 5.67007 17.1074 5.95773 17.1215 6.24431H2.23436C2.24876 5.95773 2.2556 5.67007 2.27253 5.38421C2.34057 4.25156 3.28852 3.3641 4.43124 3.3641H5.35759V4.08415C5.35759 4.48162 5.68018 4.8042 6.07765 4.8042Z"
      fill="white"
    />
    <path
      d="M6.07788 12.0047C6.67439 12.0047 7.15796 11.5212 7.15796 10.9247C7.15796 10.3281 6.67439 9.84457 6.07788 9.84457C5.48137 9.84457 4.9978 10.3281 4.9978 10.9247C4.9978 11.5212 5.48137 12.0047 6.07788 12.0047Z"
      fill="white"
    />
    <path
      d="M9.67798 12.0047C10.2745 12.0047 10.7581 11.5212 10.7581 10.9247C10.7581 10.3281 10.2745 9.84457 9.67798 9.84457C9.08147 9.84457 8.5979 10.3281 8.5979 10.9247C8.5979 11.5212 9.08147 12.0047 9.67798 12.0047Z"
      fill="white"
    />
    <path
      d="M6.07788 15.605C6.67439 15.605 7.15796 15.1214 7.15796 14.5249C7.15796 13.9284 6.67439 13.4449 6.07788 13.4449C5.48137 13.4449 4.9978 13.9284 4.9978 14.5249C4.9978 15.1214 5.48137 15.605 6.07788 15.605Z"
      fill="white"
    />
    <path
      d="M13.2783 12.0047C13.8748 12.0047 14.3584 11.5212 14.3584 10.9247C14.3584 10.3281 13.8748 9.84457 13.2783 9.84457C12.6818 9.84457 12.1982 10.3281 12.1982 10.9247C12.1982 11.5212 12.6818 12.0047 13.2783 12.0047Z"
      fill="white"
    />
    <path
      d="M13.2783 15.605C13.8748 15.605 14.3584 15.1214 14.3584 14.5249C14.3584 13.9284 13.8748 13.4449 13.2783 13.4449C12.6818 13.4449 12.1982 13.9284 12.1982 14.5249C12.1982 15.1214 12.6818 15.605 13.2783 15.605Z"
      fill="white"
    />
    <path
      d="M9.67798 15.605C10.2745 15.605 10.7581 15.1214 10.7581 14.5249C10.7581 13.9284 10.2745 13.4449 9.67798 13.4449C9.08147 13.4449 8.5979 13.9284 8.5979 14.5249C8.5979 15.1214 9.08147 15.605 9.67798 15.605Z"
      fill="white"
    />
  </svg>
);
const Notification = () => (
  <div className={Style.notification}>
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="28" viewBox="0 0 25 28" fill="none">
      <path
        d="M24.1203 20.549C23.9746 19.0319 22.6204 17.8406 21.0434 17.8406C20.8548 17.8406 20.7048 17.6906 20.7048 17.502V12.4539C20.7048 8.70421 18.305 5.43451 15.001 4.2132C15.0395 4.02033 15.0609 3.82324 15.0609 3.62608C15.061 2.01059 13.7497 0.699249 12.1341 0.699249C10.5185 0.699249 9.2072 2.01059 9.2072 3.62616C9.2072 3.81902 9.2286 4.01182 9.26288 4.19609C8.1273 4.59893 7.07738 5.23743 6.18175 6.10307C4.49333 7.73151 3.56343 9.92131 3.56343 12.2697V17.5021C3.56343 17.6864 3.41343 17.8407 3.22486 17.8407C1.64357 17.8407 0.293655 19.032 0.14795 20.549C0.0665103 21.3933 0.34511 22.2332 0.915048 22.8588C1.48069 23.4802 2.29066 23.8402 3.13483 23.8402H8.30299C8.51725 25.7643 10.1543 27.2685 12.1341 27.2685C14.1139 27.2685 15.751 25.7643 15.9652 23.8402H21.1334C21.9776 23.8402 22.7875 23.4802 23.3532 22.8588C23.9188 22.2331 24.2017 21.3932 24.1203 20.549ZM12.1341 2.41336C12.8026 2.41336 13.3468 2.9576 13.3468 3.62608C13.3468 3.68176 13.3382 3.73322 13.3297 3.78889C13.0382 3.74603 12.7425 3.71604 12.4468 3.70745C11.9369 3.69028 11.4355 3.72463 10.9384 3.79319C10.9298 3.73751 10.9212 3.68606 10.9212 3.63038C10.9214 2.9576 11.4656 2.41336 12.1341 2.41336ZM12.1341 25.5542C11.1014 25.5542 10.2357 24.8171 10.0343 23.8401H14.2339C14.0326 24.8172 13.1669 25.5542 12.1341 25.5542ZM22.0847 21.706C21.8361 21.976 21.5019 22.1259 21.1333 22.1259H3.1349C2.76634 22.1259 2.43214 21.9759 2.18352 21.706C1.93498 21.4361 1.81926 21.0803 1.85784 20.7118C1.91781 20.0648 2.5221 19.5548 3.22915 19.5548C4.36051 19.5548 5.28183 18.6335 5.28183 17.5021V12.2697C5.28183 10.3927 6.02746 8.64002 7.37737 7.33726C8.66295 6.09026 10.3429 5.41312 12.1341 5.41312C12.2198 5.41312 12.3012 5.41312 12.3869 5.41741C16.0252 5.54594 18.9907 8.70428 18.9907 12.454V17.5021C18.9907 18.6335 19.912 19.5548 21.0434 19.5548C21.7504 19.5548 22.3547 20.0648 22.4147 20.7118C22.449 21.0803 22.3332 21.4318 22.0847 21.706Z"
        fill="white"
      />
      <path
        d="M1.03435 10.9842C1.4286 10.9842 1.78427 10.7099 1.87 10.3114C2.28565 8.4301 3.22414 6.71599 4.59546 5.35327C4.92974 5.01899 4.92974 4.47474 4.59546 4.14054C4.26118 3.80626 3.71694 3.80626 3.38273 4.14054C1.78005 5.73456 0.678675 7.74444 0.194471 9.94705C0.0916328 10.4099 0.383043 10.8684 0.845849 10.967C0.914407 10.9799 0.974378 10.9842 1.03435 10.9842Z"
        fill="white"
      />
      <path
        d="M23.2321 10.984C23.2921 10.984 23.3564 10.9754 23.4164 10.9626C23.8792 10.8597 24.1706 10.4055 24.0678 9.94264C23.5835 7.73997 22.4822 5.73445 20.8794 4.13599C20.5451 3.80172 20.0009 3.80172 19.6667 4.13599C19.3325 4.47027 19.3324 5.01451 19.6667 5.34872C21.0337 6.71145 21.9765 8.42563 22.3921 10.3068C22.4822 10.7097 22.8379 10.984 23.2321 10.984Z"
        fill="white"
      />
    </svg>
    <span className={Style['notification-badge']}>5</span>
  </div>
);
const Header: React.FC<HeaderProps> = ({
  title = 'Dashboard Overview',
  dropdown,
  setGroupID = (groupID: string | null) => {},
  getGroupName = (groupID: string | null) => '',
  showBackButton = false,
  backLink = null,
  sourceOptions,
  selectedSource,
  onSourceChange,
}) => {
  const [isModal2Open, setIsModal2Open] = useState(false);
  const [open, setOpen] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [tenantOptions, setTenantOptions] = useState<(string | GroupedOptions)[]>([]);
  const [selectedDropdownValue, setSelectedDropdownValue] = useState<string | undefined>();
  const [groupsMap, setGroupsMap] = useState<Record<string, string>>({});

  const closeModal2 = () => setIsModal2Open(false);

  const navigate = useNavigate();
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { getTenant } = useTenant();
  const { getGroups } = useAllGroups();
  const { unreadCount } = useNotifications();

  const { canEdit } = usePermissions();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleButtonClick = () => {
    navigate('/patchCenter/deviceSelect'); // Navigate to the Action page
  };

  // Group list is only needed once the "Select Group" dropdown is actually opened —
  // avoid hitting the group API on every page load when it may never be used.
  const groupsFetchedRef = useRef(false);

  const fetchTenantAndGroups = async () => {
    if (groupsFetchedRef.current) return; // already loaded (or in flight) — skip refetch
    groupsFetchedRef.current = true;

    try {
      const tenantID = selectedTenantId;

      const groupsRes = await getGroups(tenantID);
      // Remove old value if exists, then set new value
      if (localStorage.getItem('groupsRes')) {
        localStorage.removeItem('groupsRes');
      }
      localStorage.setItem('groupsRes', JSON.stringify(groupsRes));

      const tenantData = groupsRes?.data?.[0]; // get the first tenant
      const tenantName = tenantData?.tenantName || 'Unknown Tenant';
      const groups = tenantData?.tenantGroupList?.map((g: any) => g.groupName) || [];

      const mapping: Record<string, string> = {};
      tenantData?.tenantGroupList?.forEach((g: any) => {
        mapping[g.groupName] = g.tenantGroupId; // ✅ Correct
      });
      setGroupsMap(mapping);

      setTenantOptions([
        { label: 'Tenant', options: [tenantName] },
        { label: 'Groups', options: groups },
      ]);
    } catch (error) {
      console.error('Failed to load tenant/groups', error);
      groupsFetchedRef.current = false; // allow retry on next open
    }
  };

  useEffect(() => {
    if (title !== 'Dashboard Overview') return;

    // Deep link case: a groupId is already in the URL, so we need the group list
    // right away to resolve/display it — fetch eagerly only then.
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('groupId')) {
      fetchTenantAndGroups();
    }
  }, []);

  const handleGroupSelect = (selectedValue: string) => {
    setSelectedDropdownValue(selectedValue);

    const firstTenantOption =
      tenantOptions.length > 0 && typeof tenantOptions[0] !== 'string' && tenantOptions[0].options.length > 0
        ? tenantOptions[0].options[0]
        : null;

    const queryParams = new URLSearchParams(location.search);

    if (selectedValue === firstTenantOption) {
      queryParams.delete('groupId');
      navigate(`?${queryParams.toString()}`, { replace: true });
      setGroupID(null);
      getGroupName(null); // Pass null when selecting tenant
    } else {
      const groupId = groupsMap[selectedValue];
      queryParams.set('groupId', groupId);
      navigate(`?${queryParams.toString()}`, { replace: true });
      setGroupID(groupId);
      getGroupName(selectedValue); // Pass the group name
    }
  };

  // --- Load groupId from query param on mount ---
  useEffect(() => {
    if (!tenantOptions.length) return;

    const queryParams = new URLSearchParams(location.search);
    const groupIdFromURL = queryParams.get('groupId');

    const firstTenantOption =
      tenantOptions.length > 0 && typeof tenantOptions[0] !== 'string' && tenantOptions[0].options.length > 0
        ? tenantOptions[0].options[0]
        : null;

    // No group → select Tenant in dropdown
    if (!groupIdFromURL) {
      if (firstTenantOption) {
        setSelectedDropdownValue(firstTenantOption);
      }
      setGroupID(null);
      getGroupName(null);
      return;
    }

    // Find group name from groupsMap (reverse lookup)
    for (const [groupName, id] of Object.entries(groupsMap)) {
      if (id === groupIdFromURL) {
        setGroupID(groupIdFromURL);
        setSelectedDropdownValue(groupName);
        getGroupName(groupName); // Pass the group name
        return;
      }
    }
  }, [tenantOptions, groupsMap, location.search]);

  const handleLogout = () => {
    logoutUser(auth.userManager);
  };

  return (
    <div className={`${Style.header} flex p-5 justify-between`} ref={dropdownRef}>
      <div className="flex items-center gap-3">
        {showBackButton && (
          <button
            onClick={() => backLink ?? navigate(-1)}
            className="flex items-center px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded-lg shadow-md transition"
          >
            <ArrowLeft size={20} className="mr-1" />
          </button>
        )}
        <div className={`${Style.title}`}>{title}</div>
      </div>
      <div className="flex gap-[20px] items-center">
        {dropdown === 1 && (
          <div className="flex gap-[10px]">
            <Dropdown
              icon={calenderIcon}
              label="Select Group"
              options={tenantOptions}
              onSelect={handleGroupSelect}
              value={selectedDropdownValue}
              onOpen={fetchTenantAndGroups}
            />
          </div>
        )}

        {!dropdown && canEdit('remediationPlan') && (
          <div className="flex items-center space-x-2">
            <button
              className="flex items-center space-x-2 bg-red-700 text-white px-4 py-2 rounded-lg shadow-md hover:bg-red-800 transition"
              onClick={() => {
                handleButtonClick();
              }}
              style={{ marginRight: '25px' }}
            >
              <PlusCircle size={18} />
              <span>Create New</span>
            </button>
          </div>
        )}

        {!!sourceOptions?.length && (
          <div className="min-w-[170px]">
            <Dropdown label="Source" options={sourceOptions} onSelect={onSourceChange} value={selectedSource} />
          </div>
        )}

        <div className="flex gap-[10px] mr-[50px] items-center">
          {/* Notification Bell Icon */}
          <div className="relative">
            <button
              onClick={() => setNotificationPanelOpen(true)}
              className="relative p-2 hover:bg-gray-700 rounded-lg transition"
              title="Notifications"
            >
              <Bell className="w-6 h-6 text-gray-300 hover:text-white" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* Logout Icon */}
          <div className="relative">
            <button onClick={() => setOpen((prev) => !prev)} className="p-2 hover:bg-gray-700 rounded-lg transition" title="Logout">
              <LogOut className="w-6 h-6 text-gray-300 hover:text-red-600" />
            </button>

            {open && (
              <div className="absolute right-0 mt-2 w-44 rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-10 z-50">
                <div className="py-2">
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-red-500 hover:text-white rounded-md transition duration-200"
                  >
                    Logout
                    <LogOut className="w-5 h-5 ml-2" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification Panel */}
      <NotificationPanel isOpen={notificationPanelOpen} onClose={() => setNotificationPanelOpen(false)} />

      {isModal2Open && <PlanActions closeModal2={closeModal2} />}
    </div>
  );
};

export default Header;
